import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'

type GhaAmenityType = 'parking' | 'wifi' | 'breakfast'

const GHA_AMENITY_PRIORITY: Array<GhaAmenityType> = ['breakfast', 'parking', 'wifi']
const GHA_AMENITY_KEYWORDS: Record<GhaAmenityType, Array<string>> = {
  parking: ['parking', 'valet', 'self parking', 'car park', 'garage'],
  wifi: ['wifi', 'wi fi', 'internet', 'broadband', 'wireless', 'wlan'],
  breakfast: ['breakfast', 'half board', 'full board', 'all inclusive'],
}
const GHA_AMENITY_SYMBOLS: Record<GhaAmenityType, Array<string>> = {
  parking: ['parking', 'underground_parking', 'p_in_circle'],
  wifi: ['wifi', 'wifi_signal', 'internet_connection'],
  breakfast: ['breakfast', 'coffee_cup', 'cloche'],
}

function normalizeAmenityText(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ')
}

function getGhaAmenityType(amenity: Pick<App.OfferInclusion, 'description' | 'symbol'>): GhaAmenityType | undefined {
  const text = normalizeAmenityText(amenity.description)
  const symbol = amenity.symbol?.toLowerCase()

  const hasMatch = (type: GhaAmenityType) => (
    GHA_AMENITY_KEYWORDS[type].some(keyword => text.includes(keyword)) ||
    (!!symbol && GHA_AMENITY_SYMBOLS[type].includes(symbol))
  )

  if (hasMatch('parking')) return 'parking'
  if (hasMatch('wifi')) return 'wifi'
  if (hasMatch('breakfast')) return 'breakfast'
}

function mapPopularFacilitiesToAmenities(popularFacilities: Array<string>): Array<App.OfferInclusion> {
  return popularFacilities.map((facility) => ({
    id: facility,
    description: facility,
    symbol: determineOfferFeatureSymbol(facility),
  }))
}

export default function getPrioritizedBedbankAmenities({
  shownRateInclusions = [],
  popularFacilities,
}: {
  shownRateInclusions?: Array<App.OfferInclusion>
  popularFacilities: Array<string>
}): Array<App.OfferInclusion> {
  const allAmenities = [
    ...shownRateInclusions,
    ...mapPopularFacilitiesToAmenities(popularFacilities),
  ]

  const seen = new Set<string>()
  const deduped: Array<{ amenity: App.OfferInclusion, ghaAmenityType: GhaAmenityType | undefined }> = []

  allAmenities.forEach((amenity) => {
    const ghaAmenityType = getGhaAmenityType(amenity)
    const dedupeKey = ghaAmenityType
      ? `gha:${ghaAmenityType}`
      : `text:${normalizeAmenityText(amenity.description)}`

    if (seen.has(dedupeKey)) return
    seen.add(dedupeKey)
    deduped.push({ amenity, ghaAmenityType })
  })

  const prioritizedAmenities = new Map<GhaAmenityType, App.OfferInclusion>()
  const restAmenities: Array<App.OfferInclusion> = []

  deduped.forEach(({ amenity, ghaAmenityType }) => {
    if (ghaAmenityType) {
      prioritizedAmenities.set(ghaAmenityType, amenity)
      return
    }
    restAmenities.push(amenity)
  })

  return [
    ...GHA_AMENITY_PRIORITY.flatMap(type => prioritizedAmenities.get(type) ? [prioritizedAmenities.get(type)!] : []),
    ...restAmenities,
  ]
}
